import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createAnnouncement,
  updateAnnouncement,
} from '../../../api/announcements.api';
import {
  ErrorToast,
  SuccessToast,
} from '../../../adminComponents/Toasts/Toasts';
import { handleError } from '../../../helpers/api';
import type { Announcement } from '../../../types/api/announcements.types';
import {
  buildAnnouncementFormData,
  draftFromAnnouncement,
  firstInvalidStep,
  listMembers,
  stepError,
  EMPTY_DRAFT,
  type AnnouncementDraft,
  type UpdateAnnouncementDraft,
} from '../helpers/announcementPayload';
import { membersNeedingCopy } from '../helpers/announcementAudience';
import { copyStoredMedia } from '../helpers/copyStoredMedia';
import {
  AnnouncementStep,
  LAST_ANNOUNCEMENT_STEP,
} from '../helpers/announcementSteps';
import { useAudienceGroups } from './useAudienceGroups';

type Args = {
  /** The announcement being edited, or null when creating a new one. */
  announcement: Announcement | null;
  isOpen: boolean;
  /** Runs after a successful create or update, with the modal still open. */
  onSaved: () => void;
};

/**
 * Everything the create/edit wizard is: which step it is on, what has been
 * typed, which audience is selected, and what it sends.
 *
 * All of it moves together - the step is only meaningful against the draft it
 * validates, and the draft is only sendable with the audience - so reading
 * them as separate pieces of state in the modal hid that the step was the one
 * thing standing between an empty title and a request.
 */
export const useAnnouncementForm = ({
  announcement,
  isOpen,
  onSaved,
}: Args) => {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<AnnouncementDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const audience = useAudienceGroups(announcement, isOpen);

  useEffect(() => {
    if (!isOpen) return;

    setStep(0);
    setDraft(announcement ? draftFromAnnouncement(announcement) : EMPTY_DRAFT);
  }, [isOpen, announcement]);

  const update = useCallback<UpdateAnnouncementDraft>(
    (key, value) => setDraft((prev) => ({ ...prev, [key]: value })),
    []
  );

  const goBack = useCallback(
    () => setStep((prev) => Math.max(prev - 1, 0)),
    []
  );

  // Backwards only. Jumping forward would skip goNext, and with it the
  // validation that keeps an empty title out of the review step.
  const goTo = useCallback(
    (target: number) => setStep((prev) => (target < prev ? target : prev)),
    []
  );

  const goNext = useCallback(() => {
    const message = stepError(
      step,
      draft,
      audience.selection,
      audience.missingMedia
    );
    if (message) return ErrorToast(message);

    setStep((prev) => Math.min(prev + 1, LAST_ANNOUNCEMENT_STEP));
  }, [step, draft, audience.selection, audience.missingMedia]);

  // What still stands between this draft and a save. Held here rather than
  // only checked on submit so the last step can disable its own button and
  // say why, instead of taking the click and bouncing back with a toast.
  const invalid = useMemo(
    () => firstInvalidStep(draft, audience.selection, audience.missingMedia),
    [draft, audience.selection, audience.missingMedia]
  );

  const submit = useCallback(async () => {
    // Review is reachable without passing through a step, so the whole draft
    // is checked here rather than trusting the way in.
    if (invalid) {
      setStep(invalid.step);
      return ErrorToast(invalid.message);
    }

    setSaving(true);

    // A platform added to a group that shows a saved picture gets that
    // picture: the API takes only files, so it is downloaded and sent for the
    // new targets. If that fails the admin picks the file by hand, as before.
    const needCopy = membersNeedingCopy(
      audience.selection,
      audience.storedMedia
    );
    let copies = new Map<string, File>();
    if (needCopy.length) {
      try {
        copies = await copyStoredMedia(needCopy);
      } catch {
        setSaving(false);
        setStep(AnnouncementStep.audience);
        return ErrorToast(
          `Could not reuse the saved image for ${listMembers(
            needCopy
          )}. Pick the file again for that group.`
        );
      }
    }

    try {
      const body = buildAnnouncementFormData(draft, audience.selection, copies);

      if (announcement) {
        await updateAnnouncement(announcement.id, body);
        SuccessToast('Announcement updated successfully');
      } else {
        await createAnnouncement(body);
        SuccessToast('Announcement created successfully');
      }

      onSaved();
    } catch (error) {
      // Shown, not swallowed: the image rule is enforced per target on the
      // server too, and its message names the exact pair that failed.
      ErrorToast(handleError(error));
    } finally {
      setSaving(false);
    }
  }, [
    announcement,
    draft,
    audience.selection,
    audience.storedMedia,
    invalid,
    onSaved,
  ]);

  return {
    step,
    draft,
    update,
    audience,
    saving,
    goBack,
    goNext,
    goTo,
    submit,
    blocker: invalid?.message ?? null,
    isEditing: !!announcement,
  };
};
